import React, { useState, useRef, useEffect } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Menu, LogIn, User as UserIcon, Settings as SettingsIcon, LogOut } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const pageTitles = {
  "/":                  "Dashboard",
  "/dashboard":         "Dashboard",
  "/events":            "Financial Journal",
  "/financial-journal": "Financial Journal",
  "/stocks":            "Stock Analysis",
  "/stock-analysis":    "Stock Analysis",
  "/morning-brief":     "Morning Brief",
  "/settings":          "Settings",
};

/** Dropdown for logged-in users — Profile / Account / Logout. */
function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fullName = user?.full_name || "User";
  const email = user?.email || "";
  const initials =
    fullName.split(/\s+/).filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "FI";
  const handle = (email.split("@")[0] || fullName).toLowerCase();

  return (
    <div ref={rootRef} className="relative">
      <button
        data-testid="topbar-user-menu"
        onClick={() => setOpen((o) => !o)}
        className="hidden sm:flex items-center gap-2 pl-2 border-l border-fij-border h-8 hover:opacity-90"
      >
        <div
          data-testid="topbar-avatar"
          className="w-7 h-7 rounded-sm bg-fij-green/10 border border-fij-green/30 grid place-items-center text-fij-green font-mono text-xs"
        >
          {initials}
        </div>
        <div className="font-mono text-[10px] leading-tight max-w-[10rem] text-left">
          <div className="text-fij-text truncate" data-testid="topbar-handle">{handle}</div>
          <div className="text-fij-textMuted">FIJ MEMBER</div>
        </div>
      </button>

      {open && (
        <div
          data-testid="user-menu-dropdown"
          className="absolute right-0 top-10 w-56 bg-fij-surface border border-fij-border rounded-sm shadow-xl z-50 overflow-hidden"
        >
          <div className="px-3 py-2.5 border-b border-fij-border">
            <div className="font-mono text-xs text-fij-text truncate">{fullName}</div>
            <div className="font-mono text-[10px] text-fij-textMuted truncate">{email}</div>
          </div>
          <Link
            to="/settings"
            data-testid="user-menu-profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 font-mono text-xs text-fij-textDim hover:text-fij-green hover:bg-fij-surfaceHover"
          >
            <UserIcon size={13} /> Profile
          </Link>
          <Link
            to="/settings"
            data-testid="user-menu-account"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 font-mono text-xs text-fij-textDim hover:text-fij-green hover:bg-fij-surfaceHover"
          >
            <SettingsIcon size={13} /> Account
          </Link>
          <button
            data-testid="user-menu-logout"
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2 px-3 py-2 font-mono text-xs text-fij-textDim hover:text-fij-red hover:bg-fij-surfaceHover border-t border-fij-border"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      )}
    </div>
  );
}

export const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();

  const handleLogout = () => { signOut(); };

  // derive title from path (handle /events/:id)
  let title = pageTitles[location.pathname];
  if (!title && location.pathname.startsWith("/events/")) title = "Event Details";
  if (!title) title = "FIJ";

  return (
    <div className="flex min-h-screen bg-fij-bg text-fij-text">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        onLogout={handleLogout}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        user={user}
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header
          data-testid="topbar"
          className="sticky top-0 z-30 h-14 bg-fij-bg/80 backdrop-blur border-b border-fij-border flex items-center gap-2 px-3 sm:px-6"
        >
          <button
            data-testid="topbar-mobile-menu"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden grid place-items-center w-9 h-9 rounded-sm text-fij-textDim hover:text-fij-green hover:bg-fij-surface"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>

          <div className="flex items-baseline gap-3 min-w-0">
            <Link
              to="/"
              data-testid="topbar-brand-home"
              aria-label="FIJ Terminal — Home"
              className="font-mono text-xs text-fij-textMuted hidden sm:inline hover:text-fij-green transition-colors"
            >
              FIJ //
            </Link>
            <h1 className="font-mono text-sm sm:text-base text-fij-text truncate" data-testid="topbar-title">
              {title}
            </h1>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {user ? (
              <UserMenu user={user} onLogout={handleLogout} />
            ) : (
              <Link
                to="/login"
                data-testid="topbar-signin"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-fij-border hover:border-fij-green/50 hover:text-fij-green font-mono text-[11px] text-fij-textDim transition-colors"
              >
                <LogIn size={12} /> SIGN IN
              </Link>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>

        {/* App footer — legal links, always at page bottom. */}
        <footer
          data-testid="app-footer"
          className="border-t border-fij-border/60 px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] text-fij-textMuted"
        >
          <span>© 2026 FIJ · Financial Impact Journal</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy"  data-testid="app-footer-privacy" className="hover:text-fij-green">Privacy Policy</Link>
            <Link to="/terms"    data-testid="app-footer-terms"   className="hover:text-fij-green">Terms &amp; Conditions</Link>
            <Link to="/contact"  data-testid="app-footer-contact" className="hover:text-fij-green">Contact</Link>
          </div>
        </footer>
      </div>
    </div>
  );
};
